import { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { QUALIFICATION_VAL_LABEL_MAP } from 'data';
import { IDocument, BirthCertificateDoc } from '../../../../types';

import PassportModal from '../../modals/passport';
import ATASModal from '../../modals/atas';
import AcademicTranscriptModal from '../../modals/academicTranscript';
import AcademicCertificateModal from '../../modals/academicCertificate';
import CASModal from '../../modals/cas';
import ParentalConsentModal from '../../modals/parentalConsent';
import BirthCertificateModal from '../../modals/birthCertificate';
import BirthCertificateTranslationModal from '../../modals/financeDocs/birthCertificateTranslation';
import PreviousCASDocumentModal from '../../modals/previousCASDocument';
import EnglishTestModal from '../../modals/englishTest';
import PostStudyLetterModal from '../../modals/postStudyLetter';
import CustomRequestModal from '../../modals/customRequest';

import ATAS from '../../../molecules/documentSection/atas';
import CAS from '../../../molecules/documentSection/cas';
import Passport from '../../../molecules/documentSection/passport';
import StudyVisa from '../../../molecules/documentSection/study-visa';
import VisaRefusal from '../../../molecules/documentSection/visa-refusal';
import NonUKVisaRefusal from '../../../molecules/documentSection/non-uk-visa-refusal';
import PreviousStudy from '../../../molecules/documentSection/previousStudy';
import EnglishTest from '../../../molecules/documentSection/english-test';
import ParentalConsent from '../../../molecules/documentSection/parental-consent';
import TBTest from '../../../molecules/documentSection/tb-test';
import PostStudyLetter from '../../../molecules/documentSection/post-study-letter';
import OtherVisa from '../../../molecules/documentSection/other-visa';
import CustomRequestDocument from '../../../molecules/documentSection/customRequest';
import NoDocuments from '../../../atoms/no-doc-placeholder';
import { createNavigateToAction } from '../../../../middleware/actions';
import { getStudentDocs, saveDocuments } from '../../../../store/student/actions';
import {
  noBiometrics,
  updateVisaRefusalMetaData,
  updateATASPending,
  updateTBTestMetaData,
  toggleAcknowledge,
  addRequiredDocument
} from '../../../../store/student/documents/actions';
import * as uiActions from '../../../../store/ui/actions';
import { hasAnyMatchingDocs } from '../../../../utilities/documents';

import {
  PageHeader,
  VisaRefusalModal,
  BiometricsModal,
  PreviousUKVisaModal,
  TBTestModal,
  uiTypes
} from 'shared-components';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../../../store';
import { useIsSectionLocked } from '../../../../hooks/useIsSectionLocked';
import { SectionId } from '../../../molecules/Sidebar/types';
import { LOCKED_SECTION_SUMMARY } from '../../../../utilities/constants';
import EnglishQualification from '../../../molecules/documentSection/english-qualification';
import EnglishQualificationModal from '../../modals/englishQualification';
import PageSubtext from '../../../atoms/PageSubtext';
import SectionHeader from '../../../atoms/SectionHeader';
import { titleAppend } from '../../../../utilities/head';
import passportExample from '../../../../assets/images/sample-passport.jpg';
import FileUploadLocal from '../../modals/fileUpload/FileUploadLocal';

const ENGLISH_TEST_NOT_REQD_REASON_ANSWER = 'Previous test';

const DocumentDashboard = () => {
  const dispatch = useDispatch();
  const documents = useSelector((state: ApplicationState) => state.student.documents);
  const courses = useSelector((state: ApplicationState) => state.student.offer.courses);
  const personalInfo = useSelector((state: ApplicationState) => state.student.personalInfo);
  const offer = useSelector((state: ApplicationState) => state.student.offer);
  const history = useSelector((state: ApplicationState) => state.student.history);
  const summary = useSelector((state: ApplicationState) => state.student.summary);
  const openModal = useSelector((state: ApplicationState) => state.ui.openModal);
  const customParentalConsentFormLink = useSelector(
    (state: ApplicationState) => state.config.customParentalConsentFormLink
  );
  const hasRequiredDocs = hasAnyMatchingDocs(documents, 'required');
  const hasInfoOnlyDocs = hasAnyMatchingDocs(documents, 'infoOnly');

  const title = useSelector((state: ApplicationState) => state.config.sections.GeneralDocument.summaryLabel);
  const summaryText = useSelector((state: ApplicationState) => state.config.sections.GeneralDocument.summary);

  useEffect(() => {
    dispatch(getStudentDocs());
  }, [dispatch]);

  const courseEndDate =
    courses && courses.length
      ? courses
          .filter(item => {
            return item.isAtas;
          })
          .map(item => {
            return item.endDate;
          })
          .join(',')
      : '';

  const isSectionLocked = useIsSectionLocked(SectionId.GENERAL_DOC, offer.status);

  const onResume = (url: string) => {
    dispatch(createNavigateToAction(url));
  };
  const openDocumentModal = (
    sponsorType: string | null,
    sponsorKey: string | null,
    docType: string | null,
    docKey: string | null,
    isInfoOnly: boolean,
    modalTitle?: string
  ) => {
    dispatch(uiActions.openDocumentModal(sponsorType, sponsorKey, docType, docKey, isInfoOnly, modalTitle));
  };
  const openUploadModal = (
    sponsorType: string | null,
    sponsorKey: string | null,
    docType: string | null,
    docKey: string | null,
    modalTitle: string,
    fileNames: string[],
    metaData?: any
  ) => {
    dispatch(uiActions.openUploadModal(sponsorType, sponsorKey, docType, docKey, modalTitle, fileNames, metaData));
  };
  const closeModal = () => {
    dispatch(uiActions.closeModal());
  };
  const onContinue = () => {
    dispatch(saveDocuments(openModal?.sponsorType, openModal?.sponsorKey, openModal?.docType, openModal?.docKey));
    dispatch(uiActions.closeModal());
  };
  const onNoBiometrics = (
    sponsorType: string,
    sponsorKey: string | null,
    docType: string | null,
    docKey: string | null
  ) => {
    dispatch(noBiometrics(sponsorType, sponsorKey, docType, docKey));
    dispatch(uiActions.closeModal());
  };
  const onVisaRefusalMetaDataUpdate = (
    sponsorType: string,
    sponsorKey: string,
    docType: string,
    docKey: string,
    metadata: any
  ) => dispatch(updateVisaRefusalMetaData(sponsorType, sponsorKey, docType, docKey, metadata));
  const onTickATASCheckbox = (docKey: string, isAtasPending: boolean) =>
    dispatch(updateATASPending(docKey, isAtasPending));
  const onTBTestMetaUpdate = (sponsorType: string, docKey: string, metaData: uiTypes.ITBTestMetaData) =>
    dispatch(updateTBTestMetaData(sponsorType, docKey, metaData));

  const onAddRequiredDocument = (
    sponsorType: string,
    sponsorKey: string | null,
    docType: string | null,
    docKey: string | null,
    newDocType: string,
    metadata?: any
  ) => {
    dispatch(addRequiredDocument(sponsorType, sponsorKey, docType, docKey, newDocType, metadata));
  };
  const onAcknowledgeToggle = (
    sponsorType: string,
    sponsorKey: string | null,
    docType: string | null,
    docKey: string,
    status: string,
    infoOnlyRequirementsOption?: string,
    metadata?: any
  ) =>
    dispatch(toggleAcknowledge(sponsorType, sponsorKey, docType, docKey, status, infoOnlyRequirementsOption, metadata));

  const renderDocs = (infoOnly = false) => {
    const qualificationAnswer =
      personalInfo.englishTestNotReqdReason === ENGLISH_TEST_NOT_REQD_REASON_ANSWER &&
      personalInfo.qualification &&
      !personalInfo.qualificationOther
        ? QUALIFICATION_VAL_LABEL_MAP[personalInfo.qualification]
        : personalInfo.qualificationOther;

    return (
      <>
        <Helmet>
          <title>General Documents {titleAppend}</title>
        </Helmet>

        <ATAS
          infoOnly={infoOnly}
          documents={documents.atas}
          onResume={onResume}
          onClick={(docKey: string, infoOnly: boolean) => openDocumentModal(null, null, 'atas', docKey, infoOnly)}
        />
        <StudyVisa
          infoOnly={infoOnly}
          documents={documents.studentVisas}
          onResume={onResume}
          onVisaClick={(sponsorKey: string, docKey: string, infoOnly: boolean) =>
            openDocumentModal('studentVisas', sponsorKey, 'visa', docKey, infoOnly)
          }
          onCasClick={(sponsorKey: string, docKey: string, infoOnly: boolean) =>
            openDocumentModal('studentVisas', sponsorKey, 'cas', docKey, infoOnly)
          }
          onBiometricClick={(sponsorKey: string, docKey: string, infoOnly: boolean) =>
            openDocumentModal('studentVisas', sponsorKey, 'biometric', docKey, infoOnly)
          }
        />
        <OtherVisa
          infoOnly={infoOnly}
          documents={documents.otherVisas}
          onResume={onResume}
          onVisaClick={(sponsorKey: string, docKey: string, infoOnly: boolean) =>
            openDocumentModal('otherVisas', sponsorKey, 'visa', docKey, infoOnly)
          }
          onBiometricClick={(sponsorKey: string, docKey: string, infoOnly: boolean) =>
            openDocumentModal('otherVisas', sponsorKey, 'biometric', docKey, infoOnly)
          }
        />
        <VisaRefusal
          infoOnly={infoOnly}
          documents={documents.visaRefusals}
          onResume={onResume}
          onClick={(sponsorKey: string, docKey: string, infoOnly: boolean) =>
            openDocumentModal('visaRefusals', sponsorKey, null, docKey, infoOnly)
          }
        />
        <NonUKVisaRefusal
          infoOnly={infoOnly}
          documents={documents.nonUkVisaRefusals}
          onResume={onResume}
          onClick={(sponsorKey: string, docKey: string, infoOnly: boolean) =>
            openDocumentModal('nonUkVisaRefusals', sponsorKey, null, docKey, infoOnly)
          }
        />
        <CAS
          infoOnly={infoOnly}
          documents={documents.cas}
          onResume={onResume}
          onClick={(docKey: string, infoOnly: boolean) => openDocumentModal(null, null, 'cas', docKey, infoOnly)}
        />
        <Passport
          infoOnly={infoOnly}
          passportStatus={personalInfo.passportStatus}
          passportExpectedDate={personalInfo.passportExpectedDate}
          passportExpiryDate={personalInfo.passportExpiryDate}
          courses={offer.courses ?? []}
          documents={documents.passport}
          onResume={onResume}
          onClick={(docType: string, docKey: string, infoOnly: boolean) =>
            openDocumentModal('passport', null, docType, docKey, infoOnly)
          }
        />
        <PreviousStudy
          infoOnly={infoOnly}
          courseName={history.lastCourse !== 'Other' ? history.lastCourse : history.lastCourseOther}
          courseYear={history.lastCourseYear}
          academicTranscripts={documents.academicTranscript}
          academicCertificates={documents.academicCertificate}
          onResume={onResume}
          onAcademicTranscriptClick={(docKey: string, infoOnly: boolean) =>
            openDocumentModal(null, null, 'academicTranscript', docKey, infoOnly)
          }
          onAcademicCertificateClick={(docKey: string, infoOnly: boolean) =>
            openDocumentModal(null, null, 'academicCertificate', docKey, infoOnly)
          }
        />
        <EnglishTest
          infoOnly={infoOnly}
          englishTestDone={personalInfo.hasEnglishTest}
          englishTestType={
            personalInfo.englishTestType !== 'Other' ? personalInfo.englishTestType : personalInfo.englishTestTypeOther
          }
          documents={documents.englishTest}
          onResume={onResume}
          onClick={(docKey: string, infoOnly: boolean) =>
            openDocumentModal(null, null, 'englishTest', docKey, infoOnly)
          }
        />
        <EnglishQualification
          infoOnly={infoOnly}
          englishTestQualification={qualificationAnswer}
          qualificationOther={personalInfo.qualificationOther}
          documents={documents.englishQualification}
          onResume={onResume}
          onClick={(docKey: string, infoOnly: boolean) =>
            openDocumentModal(null, null, 'englishQualification', docKey, infoOnly)
          }
        />

        <ParentalConsent
          infoOnly={infoOnly}
          documents={documents.parentalConsent}
          birthCertificate={documents.birthCertificate}
          onResume={onResume}
          onClick={(sponsorType: string | null, docType: string, docKey: string, infoOnly: boolean) => {
            openDocumentModal(sponsorType, null, docType, docKey, infoOnly);
          }}
        />
        <TBTest
          infoOnly={infoOnly}
          documents={documents.tbTest}
          onResume={onResume}
          onClick={(docKey: string, infoOnly: boolean) => openDocumentModal(null, null, 'tbTest', docKey, infoOnly)}
        />
        <PostStudyLetter
          infoOnly={infoOnly}
          documents={documents.postStudyLetter}
          onResume={onResume}
          onClick={(docKey: string, infoOnly: boolean) =>
            openDocumentModal(null, null, 'postStudyLetter', docKey, infoOnly)
          }
        />
        <CustomRequestDocument
          infoOnly={infoOnly}
          documents={documents.customRequest}
          onResume={onResume}
          onClick={(sponsorKey: string, docKey: string, infoOnly: boolean) =>
            openDocumentModal('customRequest', sponsorKey, 'documents', docKey, infoOnly)
          }
        />
      </>
    );
  };

  const getModal = () => {
    if (openModal?.isUpload) {
      return <FileUploadLocal openModal={openModal} />;
    }

    switch (openModal?.sponsorType) {
      case 'nonUkVisaRefusals':
      case 'visaRefusals': {
        const doc = documents[openModal.sponsorType][openModal.sponsorKey][openModal.docKey];
        const modalTitle = openModal.sponsorType === 'nonUkVisaRefusals' ? 'Non-UK Visa Refusal' : 'Visa Refusal';
        return (
          <VisaRefusalModal
            docKey={openModal.docKey}
            infoOnly={!!openModal.isInfoOnly}
            docStatus={doc.status}
            onAcknowledgeToggle={(status: string) =>
              onAcknowledgeToggle(
                openModal.sponsorType,
                openModal.sponsorKey,
                openModal.docType,
                openModal.docKey,
                status
              )
            }
            complete={['UPLOADED_OK', 'APPROVED'].includes(doc.status)}
            status={doc.status}
            isNonUkVisaRefusal={openModal.sponsorType === 'nonUkVisaRefusals'}
            hasVisaRefusalDoc={doc.hasVisaRefusalDoc}
            hasNoVisaRefusalDocReason={doc.hasNoVisaRefusalDocReason}
            refusalDate={doc.refusalDate}
            fileNames={doc.fileNames}
            onContinue={onContinue}
            onUpload={(metadata?: any) =>
              openUploadModal(
                openModal.sponsorType,
                openModal.sponsorKey,
                openModal.docType,
                openModal.docKey,
                modalTitle,
                doc.fileNames,
                metadata
              )
            }
            onClose={closeModal}
            onVisaRefusalMetaDataUpdate={(metadata?: any) =>
              onVisaRefusalMetaDataUpdate(
                openModal.sponsorType,
                openModal.sponsorKey,
                openModal.docType,
                openModal.docKey,
                metadata
              )
            }
          />
        );
      }
      case 'otherVisas': {
        const otherVisaDoc = documents[openModal.sponsorType][openModal.sponsorKey];

        const doc = otherVisaDoc[openModal.docType][openModal.docKey];

        switch (openModal.docType) {
          case 'visa': {
            return (
              <PreviousUKVisaModal
                isStudentVisa={false}
                visaType={otherVisaDoc.visaType}
                year={otherVisaDoc.year}
                docKey={openModal.docKey}
                infoOnly={!!openModal.isInfoOnly}
                docStatus={doc.status}
                status={doc.status}
                onAcknowledgeToggle={(status: string) =>
                  onAcknowledgeToggle(
                    openModal.sponsorType,
                    openModal.sponsorKey,
                    openModal.docType,
                    openModal.docKey,
                    status
                  )
                }
                complete={['UPLOADED_OK', 'APPROVED'].includes(doc.status)}
                fileNames={doc.fileNames}
                onContinue={onContinue}
                onUpload={(metadata?: any) =>
                  openUploadModal(
                    openModal.sponsorType,
                    openModal.sponsorKey,
                    openModal.docType,
                    openModal.docKey,
                    'Other Visa',
                    doc.fileNames,
                    metadata
                  )
                }
                onClose={closeModal}
                startDate={doc.startDate}
                endDate={doc.endDate}
              />
            );
          }
          case 'biometric': {
            return (
              <BiometricsModal
                docKey={openModal.docKey}
                infoOnly={!!openModal.isInfoOnly}
                docStatus={doc.status}
                onAcknowledgeToggle={(status: string) =>
                  onAcknowledgeToggle(
                    openModal.sponsorType,
                    openModal.sponsorKey,
                    openModal.docType,
                    openModal.docKey,
                    status
                  )
                }
                complete={['UPLOADED_OK', 'APPROVED'].includes(doc.status)}
                fileNames={doc.fileNames}
                hasDocument={doc.hasDocument}
                onContinue={onContinue}
                onUpload={(metadata?: any) =>
                  openUploadModal(
                    openModal.sponsorType,
                    openModal.sponsorKey,
                    openModal.docType,
                    openModal.docKey,
                    'Previous Biometric Residence Permit',
                    doc.fileNames,
                    metadata
                  )
                }
                onClose={closeModal}
                onNoBiometrics={() =>
                  onNoBiometrics(openModal.sponsorType, openModal.sponsorKey, openModal.docType, openModal.docKey)
                }
              />
            );
          }
          default: {
            // should never reach here
            return null;
          }
        }
      }
      case 'passport': {
        switch (openModal.docType) {
          case 'current':
          case 'renew': {
            const doc = documents.passport[openModal.docType][openModal.docKey];
            return (
              <PassportModal
                infoOnly={!!openModal.isInfoOnly}
                docStatus={doc.status}
                onAcknowledgeToggle={(status: string) =>
                  onAcknowledgeToggle(
                    openModal.sponsorType,
                    openModal.sponsorKey,
                    openModal.docType,
                    openModal.docKey,
                    status
                  )
                }
                docNumber={doc.docNumber}
                placeOfIssue={doc.placeOfIssue}
                dateOfIssue={doc.dateOfIssue}
                dateOfExpiry={doc.dateOfExpiry}
                docType={openModal.docType}
                docKey={openModal.docKey}
                passportInvalidForCourse={doc.passportInvalidForCourse}
                complete={['UPLOADED_OK', 'APPROVED'].includes(doc.status)}
                fileNames={doc.fileNames}
                onContinue={onContinue}
                onUpload={openUploadModal}
                onClose={closeModal}
                passportUrl={passportExample}
              />
            );
          }
          default:
            return null;
        }
      }
      case 'customRequest': {
        const doc = documents[openModal.sponsorType][openModal.sponsorKey][openModal.docKey];

        return (
          <CustomRequestModal
            label={doc.label}
            description={doc.desc}
            complete={['UPLOADED_OK', 'APPROVED'].includes(doc.status)}
            fileNames={doc.fileNames}
            onContinue={onContinue}
            onUpload={() =>
              openUploadModal(
                openModal.sponsorType,
                openModal.sponsorKey,
                null,
                openModal.docKey,
                'Custom request document',
                doc.fileNames
              )
            }
            onClose={closeModal}
          />
        );
      }
      case 'birthCertificate': {
        switch (openModal.docType) {
          case 'certificate': {
            const docKey = openModal.docKey;
            const doc: BirthCertificateDoc = documents.birthCertificate.certificate[docKey];

            const { fileNames } = doc;

            return (
              <BirthCertificateModal
                infoOnly={!!openModal.isInfoOnly}
                docStatus={doc.status}
                onAcknowledgeToggle={(status: string, infoOnlyRequirementsOption?: string, metadata?: any) =>
                  onAcknowledgeToggle(
                    openModal.sponsorType,
                    openModal.sponsorKey,
                    openModal.docType,
                    openModal.docKey,
                    status,
                    infoOnlyRequirementsOption,
                    metadata
                  )
                }
                docKey={openModal.docKey}
                onContinue={closeModal}
                onClose={closeModal}
                document={doc}
                onUpload={(metadata?: any) =>
                  openUploadModal(
                    'birthCertificate',
                    null,
                    'certificate',
                    docKey,
                    'Birth Certificate',
                    fileNames,
                    metadata
                  )
                }
                onAddRequiredDocument={(metadata?: any) =>
                  onAddRequiredDocument('birthCertificate', null, 'certificate', docKey, 'translation', metadata)
                }
              />
            );
          }
          case 'translation': {
            const docKey = openModal.docKey;
            const doc: IDocument = documents.birthCertificate.translation[docKey];

            const { status, fileNames } = doc;

            return (
              <BirthCertificateTranslationModal
                infoOnly={!!openModal.isInfoOnly}
                docStatus={doc.status}
                onAcknowledgeToggle={(status: string) =>
                  onAcknowledgeToggle(
                    openModal.sponsorType,
                    openModal.sponsorKey,
                    openModal.docType,
                    openModal.docKey,
                    status
                  )
                }
                docKey={openModal.docKey}
                complete={status !== 'AWAITING'}
                fileNames={fileNames}
                onContinue={closeModal}
                onUpload={(metadata?: any) =>
                  openUploadModal(
                    'birthCertificate',
                    null,
                    'translation',
                    docKey,
                    'Birth Certificate Translation',
                    fileNames,
                    metadata
                  )
                }
                onClose={closeModal}
              />
            );
          }
          default: {
            return null;
          }
        }
      }
      case 'studentVisas': {
        const doc = documents[openModal.sponsorType][openModal.sponsorKey][openModal.docType][openModal.docKey];

        switch (openModal.docType) {
          case 'visa': {
            return (
              <PreviousUKVisaModal
                isStudentVisa={true}
                docKey={openModal.docKey}
                infoOnly={!!openModal.isInfoOnly}
                docStatus={doc.status}
                onAcknowledgeToggle={(status: string) =>
                  onAcknowledgeToggle(
                    openModal.sponsorType,
                    openModal.sponsorKey,
                    openModal.docType,
                    openModal.docKey,
                    status
                  )
                }
                complete={['UPLOADED_OK', 'APPROVED'].includes(doc.status)}
                fileNames={doc.fileNames}
                status={doc.status}
                onContinue={onContinue}
                onUpload={metadata =>
                  openUploadModal(
                    openModal.sponsorType,
                    openModal.sponsorKey,
                    openModal.docType,
                    openModal.docKey,
                    'Previous Student Visa',
                    doc.fileNames,
                    metadata
                  )
                }
                onClose={closeModal}
                startDate={doc.startDate}
                endDate={doc.endDate}
              />
            );
          }
          case 'cas': {
            return (
              <PreviousCASDocumentModal
                docKey={openModal.docKey}
                infoOnly={!!openModal.isInfoOnly}
                docStatus={doc.status}
                onAcknowledgeToggle={(status: string) =>
                  onAcknowledgeToggle(
                    openModal.sponsorType,
                    openModal.sponsorKey,
                    openModal.docType,
                    openModal.docKey,
                    status
                  )
                }
                complete={['UPLOADED_OK', 'APPROVED'].includes(doc.status)}
                fileNames={doc.fileNames}
                onContinue={onContinue}
                onUpload={() =>
                  openUploadModal(
                    openModal.sponsorType,
                    openModal.sponsorKey,
                    openModal.docType,
                    openModal.docKey,
                    'Previous CAS Document',
                    doc.fileNames
                  )
                }
                onClose={closeModal}
              />
            );
          }
          case 'biometric': {
            return (
              <BiometricsModal
                docKey={openModal.docKey}
                infoOnly={!!openModal.isInfoOnly}
                docStatus={doc.status}
                onAcknowledgeToggle={(status: string) =>
                  onAcknowledgeToggle(
                    openModal.sponsorType,
                    openModal.sponsorKey,
                    openModal.docType,
                    openModal.docKey,
                    status
                  )
                }
                complete={['UPLOADED_OK', 'APPROVED'].includes(doc.status)}
                fileNames={doc.fileNames}
                hasDocument={doc.hasDocument}
                onContinue={onContinue}
                onUpload={metadata =>
                  openUploadModal(
                    openModal.sponsorType,
                    openModal.sponsorKey,
                    openModal.docType,
                    openModal.docKey,
                    'Previous Biometric Residence Permit',
                    doc.fileNames,
                    metadata
                  )
                }
                onClose={closeModal}
                onNoBiometrics={() =>
                  onNoBiometrics(openModal.sponsorType, openModal.sponsorKey, openModal.docType, openModal.docKey)
                }
              />
            );
          }
          default: {
            // should never reach here
            return null;
          }
        }
      }

      default:
        switch (openModal?.docType) {
          case 'parentalConsent': {
            const doc = documents[openModal.docType][openModal.docKey];
            return (
              <ParentalConsentModal
                infoOnly={!!openModal.isInfoOnly}
                docStatus={doc.status}
                onAcknowledgeToggle={(status: string) =>
                  onAcknowledgeToggle('parentalConsent', null, null, openModal.docKey, status)
                }
                customDocumentLink={customParentalConsentFormLink}
                docKey={openModal.docKey}
                complete={['UPLOADED_OK', 'APPROVED', 'ACKNOWLEDGED'].includes(doc.status)}
                fileNames={doc.fileNames}
                isSigned={doc.isSigned}
                isDated={doc.isDated}
                isDocumentFixed={doc.isDocumentFixed}
                onContinue={closeModal}
                onUpload={(metadata?: any) => {
                  openUploadModal(
                    'parentalConsent',
                    null,
                    null,
                    openModal.docKey,
                    'Parental Consent Letter',
                    doc.fileNames,
                    metadata
                  );
                }}
                onClose={closeModal}
              />
            );
          }
          case 'cas': {
            const doc = documents[openModal.docType][openModal.docKey];
            return (
              <CASModal
                infoOnly={!!openModal.isInfoOnly}
                docStatus={doc.status}
                onAcknowledgeToggle={(status: string) =>
                  onAcknowledgeToggle(
                    openModal.sponsorType,
                    openModal.sponsorKey,
                    openModal.docType,
                    openModal.docKey,
                    status
                  )
                }
                docKey={openModal.docKey}
                complete={['UPLOADED_OK', 'APPROVED'].includes(doc.status)}
                fileNames={doc.fileNames}
                onContinue={onContinue}
                onUpload={openUploadModal}
                onClose={closeModal}
              />
            );
          }
          case 'atas': {
            const doc = documents[openModal.docType][openModal.docKey];

            return (
              <ATASModal
                infoOnly={!!openModal.isInfoOnly}
                docStatus={doc.status}
                onAcknowledgeToggle={(status: string) =>
                  onAcknowledgeToggle(
                    openModal.sponsorType,
                    openModal.sponsorKey,
                    openModal.docType,
                    openModal.docKey,
                    status
                  )
                }
                docKey={openModal.docKey}
                courseCAH3Code={
                  offer.courses && offer.courses.length
                    ? offer.courses
                        .filter(item => {
                          return item.isAtas;
                        })
                        .map(item => {
                          return item.cah3;
                        })
                        .join(',')
                    : ''
                }
                courseEndDate={courseEndDate}
                homeCountry={personalInfo.country}
                complete={['UPLOADED_OK', 'APPROVED'].includes(doc.status)}
                fileNames={doc.fileNames}
                onContinue={onContinue}
                onUpload={openUploadModal}
                onClose={closeModal}
                onTickATASCheckbox={(isAtasPending: boolean) => onTickATASCheckbox(openModal.docKey, isAtasPending)}
                isAtasPending={doc.isAtasPending}
              />
            );
          }
          case 'academicCertificate': {
            const doc = documents[openModal.docType][openModal.docKey];
            return (
              <AcademicCertificateModal
                infoOnly={!!openModal.isInfoOnly}
                docStatus={doc.status}
                onAcknowledgeToggle={(status: string) =>
                  onAcknowledgeToggle(
                    openModal.sponsorType,
                    openModal.sponsorKey,
                    openModal.docType,
                    openModal.docKey,
                    status
                  )
                }
                docKey={openModal.docKey}
                lastCourseName={history.lastCourse !== 'Other' ? history.lastCourse : history.lastCourseOther}
                lastCourseYear={history.lastCourseYear}
                complete={['UPLOADED_OK', 'APPROVED'].includes(doc.status)}
                fileNames={doc.fileNames}
                onContinue={onContinue}
                onUpload={openUploadModal}
                onClose={closeModal}
              />
            );
          }
          case 'academicTranscript': {
            const doc = documents[openModal.docType][openModal.docKey];
            return (
              <AcademicTranscriptModal
                infoOnly={!!openModal.isInfoOnly}
                docStatus={doc.status}
                onAcknowledgeToggle={(status: string) =>
                  onAcknowledgeToggle(
                    openModal.sponsorType,
                    openModal.sponsorKey,
                    openModal.docType,
                    openModal.docKey,
                    status
                  )
                }
                docKey={openModal.docKey}
                lastCourseName={history.lastCourse !== 'Other' ? history.lastCourse : history.lastCourseOther}
                lastCourseYear={history.lastCourseYear}
                complete={['UPLOADED_OK', 'APPROVED'].includes(doc.status)}
                fileNames={doc.fileNames}
                onContinue={onContinue}
                onUpload={openUploadModal}
                onClose={closeModal}
              />
            );
          }

          case 'englishTest': {
            const doc = documents[openModal.docType][openModal.docKey];

            return (
              <EnglishTestModal
                docKey={openModal.docKey}
                infoOnly={!!openModal.isInfoOnly}
                docStatus={doc.status}
                onAcknowledgeToggle={(status: string) =>
                  onAcknowledgeToggle(
                    openModal.sponsorType,
                    openModal.sponsorKey,
                    openModal.docType,
                    openModal.docKey,
                    status
                  )
                }
                englishTestType={
                  personalInfo.englishTestType !== 'Other'
                    ? personalInfo.englishTestTypeOther
                    : personalInfo.englishTestType
                }
                complete={['UPLOADED_OK', 'APPROVED'].includes(doc.status)}
                fileNames={doc.fileNames}
                onContinue={onContinue}
                onUpload={() =>
                  openUploadModal(
                    openModal.sponsorType,
                    openModal.sponsorKey,
                    openModal.docType,
                    openModal.docKey,
                    'English Test Results',
                    doc.fileNames
                  )
                }
                onClose={closeModal}
              />
            );
          }
          case 'englishQualification': {
            const doc = documents[openModal.docType][openModal.docKey];
            return (
              <EnglishQualificationModal
                docKey={openModal.docKey}
                infoOnly={!!openModal.isInfoOnly}
                docStatus={doc.status}
                onAcknowledgeToggle={(status: string) =>
                  onAcknowledgeToggle(
                    openModal.sponsorType,
                    openModal.sponsorKey,
                    openModal.docType,
                    openModal.docKey,
                    status
                  )
                }
                englishQualification={
                  personalInfo.qualification !== 'Other'
                    ? personalInfo.qualification || ''
                    : personalInfo.qualificationOther || ''
                }
                complete={['UPLOADED_OK', 'APPROVED'].includes(doc.status)}
                fileNames={doc.fileNames}
                onContinue={onContinue}
                onUpload={() =>
                  openUploadModal(
                    openModal.sponsorType,
                    openModal.sponsorKey,
                    openModal.docType,
                    openModal.docKey,
                    'English Qualification Results',
                    doc.fileNames
                  )
                }
                onClose={closeModal}
              />
            );
          }
          case 'tbTest': {
            const doc = documents[openModal.docType][openModal.docKey];
            return (
              <TBTestModal
                infoOnly={!!openModal.isInfoOnly}
                docStatus={doc.status}
                onAcknowledgeToggle={(status: string) =>
                  onAcknowledgeToggle(
                    openModal.sponsorType,
                    openModal.sponsorKey,
                    openModal.docType,
                    openModal.docKey,
                    status
                  )
                }
                document={doc}
                docKey={openModal.docKey}
                complete={['UPLOADED_OK', 'APPROVED'].includes(doc.status)}
                fileNames={doc.fileNames}
                onContinue={onContinue}
                onUpload={openUploadModal}
                onClose={closeModal}
                onTBTestMetaUpdate={(metaData: uiTypes.ITBTestMetaData) =>
                  onTBTestMetaUpdate(openModal.sponsorType, openModal.docKey, metaData)
                }
              />
            );
          }
          case 'postStudyLetter': {
            const doc = documents[openModal.docType][openModal.docKey];

            return (
              <PostStudyLetterModal
                docKey={openModal.docKey}
                infoOnly={!!openModal.isInfoOnly}
                docStatus={doc.status}
                onAcknowledgeToggle={(status: string) =>
                  onAcknowledgeToggle(
                    openModal.sponsorType,
                    openModal.sponsorKey,
                    openModal.docType,
                    openModal.docKey,
                    status
                  )
                }
                complete={['UPLOADED_OK', 'APPROVED'].includes(doc.status)}
                fileNames={doc.fileNames}
                onContinue={onContinue}
                onUpload={() =>
                  openUploadModal(
                    openModal.sponsorType,
                    openModal.sponsorKey,
                    openModal.docType,
                    openModal.docKey,
                    'Personal Post-Study Letter',
                    doc.fileNames
                  )
                }
                onClose={closeModal}
              />
            );
          }
        }
    }
  };

  return (
    <>
      {getModal()}
      <PageHeader dataSelector="Dashboard-Header">{title}</PageHeader>

      <PageSubtext>{isSectionLocked ? LOCKED_SECTION_SUMMARY : summaryText}</PageSubtext>

      {summary.docsRequired > 0 || hasRequiredDocs || hasInfoOnlyDocs ? (
        <>
          {hasRequiredDocs ? (
            <>
              <SectionHeader title="Please upload the following:" />

              {renderDocs()}
            </>
          ) : null}
          {hasInfoOnlyDocs ? (
            <>
              <SectionHeader
                title="Please review the following:"
                data-selector="info-only-docs-section"
                subtext="These documents do not need to be uploaded but may be requested from you during your visa
                application."
              />

              {renderDocs(true)}
            </>
          ) : null}
        </>
      ) : (
        <NoDocuments />
      )}
    </>
  );
};

export default DocumentDashboard;
